import React, { useState, useEffect, useRef, useContext } from 'react';
import { collection, addDoc, query, where, orderBy, updateDoc, onSnapshot, doc, setDoc, getDocs, getDoc, arrayUnion, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { UserContext } from '../context/UserContext';
import Layout from '../components/Layout';
import '../css/MessagesPage.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SharedPostLink from '../UI-Components/SharedPostLink/SharedPostLink';
import { Avatar } from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import SendIcon from '../assets/icons/CTA.svg';

const MessagesPage = () => {
    const navigate = useNavigate();
    //Search stuff starts here

    const [searchTerm, setSearchTerm] = useState("");
    const { user } = useContext(UserContext);
    const [suggestions, setSuggestions] = useState([]);
    const [status, setStatus] = useState("");
    const [isFriendsDropdownOpen, setIsFriendsDropdownOpen] = useState(false);
    const [showStatus, setShowStatus] = useState(false);
    const [searchParams] = useSearchParams();
    const groupId = searchParams.get('group');  
    const [isMobile, setIsMobile] = useState(true);

    const friendsDropdownRef = useRef(null); // Ref to track clicks outside dropdown
    
    //Open group chat if groupId is provided
    useEffect(() => {
        if (groupId) {
            setSelectedChat(groupId); // Open the group chat
        }
    }, [groupId]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1200);
        };

        // Set initially
        handleResize();

        // Add resize listener
        window.addEventListener("resize", handleResize);

        // Cleanup on unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Fetch user suggestions from Firestore
    useEffect(() => {
        if (searchTerm.length < 2) {
            setSuggestions([]); 
            return;
        }

        const fetchUsers = async () => {
            const searchWords = searchTerm.toLowerCase().trim().split(" "); 
            const endTerm = searchTerm.toLowerCase().trim() + "\uf8ff";
            try {
            const queries = searchWords.map((word) => 
                query(collection(db, "UserInformation"), where("searchKeyWords", "array-contains", word))
            );

            queries.push(
                query(
                    collection(db, "UserInformation"),
                    where("nameLower", ">=", searchTerm.toLowerCase().trim()),
                    where("nameLower", "<=", endTerm)
                )
            );

            const snapshots = await Promise.all(queries.map(getDocs));

            let results = snapshots.flatMap(snapshot => 
                snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            );

            const uniqueResults = Array.from(new Map(results.map(usr => [usr.id, usr])).values());

            setSuggestions(uniqueResults);
            console.log("Fetched users:", uniqueResults);
            } catch (error) {
            console.error("Error fetching user suggestions:", error);
            }
        };

        fetchUsers();
    }, [searchTerm]);

    const getUserName = async (userId) => {
        try {
            const userRef = doc(db, "UserInformation", userId);
            
            const userDoc = await getDoc(userRef);
            
            if (userDoc.exists()) {
                const userName = userDoc.data().name;
                return userName;
            } else {
                return null;
            }
        } catch (error) {
            console.error("Error getting user name:", error);
            return null;
        }
    };

    const sendFriendRequest = async (friendId) => {
        if (!user || !friendId) return;
        setIsFriendsDropdownOpen(false);
        setSearchTerm(""); // Clear input
        setSuggestions([]); // Clear suggestions

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();

            // Get friend's user data to check pending requests
            const friendReference = doc(db, "UserInformation", friendId);
            const friendDoc = await getDoc(friendReference);
            const friendData = friendDoc.data();

            if (friendId === user.uid) {
                setStatus("Cannot send friend request to yourself.");
                setShowStatus(true);
                return;
            }

            // Check if user is already friends with the friendId
            if (userData && userData.friendsList && userData.friendsList.some(friend => friend.id === friendId)) {
                setStatus("Already friends.");
                setShowStatus(true);
                return;
            }

            if (friendData && friendData.friendRequests) {
                const pendingRequest = friendData.friendRequests.find(
                    request => request.senderId === user.uid
                );
                
                if (pendingRequest) {
                    setStatus("Friend request already sent.");
                    setShowStatus(true);
                    return;
                }
            }

            console.log("sending request:..")
            const myName = await getUserName(user.uid); 
            if (!myName) {
                console.error("Friend's name not found.");
            return;
            }
            const friendRef = doc(db, "UserInformation", friendId);
            
            await updateDoc(friendRef, {
                friendRequests: arrayUnion({
                    senderId: user.uid,
                    senderName: myName,
                    timestamp: new Date().toISOString(),
                }),
            });

            console.log("sent request:..")
            setStatus("Friend request sent!");
            setShowStatus(true);
        } catch (error) {
            console.error("Error sending friend request:", error);
            setStatus("Error sending request.");
            setShowStatus(true);
        }
    };

    useEffect(() => {
        if (showStatus) {
            const timer = setTimeout(() => {
                setShowStatus(false);
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [showStatus]);

    //Search stuff ends here

    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [allFriends, setAllFriends] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [senderNames, setSenderNames] = useState({});
    const [senderProfilePics, setSenderProfilePics] = useState({});

    const dropdownRef = useRef(null); // Ref to track clicks outside dropdown

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (friendsDropdownRef.current && !friendsDropdownRef.current.contains(event.target)) {
                setIsFriendsDropdownOpen(false);       
                setSearchTerm(""); // Clear input
                setSuggestions([]); // Clear suggestions
            }
        };

        if (isDropdownOpen || isFriendsDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen, isFriendsDropdownOpen]);

    useEffect(() => {
        const fetchFriends = async () => {
            if (!user) return;
            try{
                console.log("Fetching users >>>");
                const userRef = doc(db, "UserInformation", user.uid);
                const userDoc = await getDoc(userRef);
                const userData = userDoc.data();

                if (userData && userData.friendsList) {
                    setAllFriends(userData.friendsList);
                    console.log(allFriends);
                }
            } catch (error) {
                console.error("Error fetching user's friends:", error);
            }     
        };
        fetchFriends();
    }, [user]);

    // Fetch conversations where the user is a participant
    useEffect(() => {
        if (!user) return;
        
        const q = query(collection(db, "conversations"), where("participants", "array-contains", user.uid));

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const chats = await Promise.all(snapshot.docs.map(async (docSnap) => {
                const chatData = docSnap.data();
                
                // Get last message for all chats (both group and individual)
                const messagesRef = collection(db, "conversations", docSnap.id, "messages");
                const lastMessageQuery = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
                const lastMessageSnap = await getDocs(lastMessageQuery);
                const lastMessage = lastMessageSnap.docs[0]?.data();

                if (!chatData.isGroup) {
                    // For individual chats
                    const otherUserId = chatData.participants.find(id => id !== user.uid);
                    const otherUserDoc = await getDoc(doc(db, "UserInformation", otherUserId));
                    const otherUserData = otherUserDoc.data();
                    
                    return { 
                        id: docSnap.id, 
                        chatName: otherUserData?.name || "Unknown User",
                        profilePic: otherUserData?.profilePictureUrl,
                        lastMessageTime: lastMessage?.timestamp,
                        isGroup: false,
                        userId: otherUserId,
                        ...chatData 
                    };
                }

                // For group chats
                try {
                    const groupsRef = collection(db, "groups");
                    const groupQuery = query(groupsRef, where("conversationId", "==", docSnap.id));
                    const groupSnapshot = await getDocs(groupQuery);
                    
                    if (!groupSnapshot.empty) {
                        const groupDoc = groupSnapshot.docs[0];
                        const groupData = groupDoc.data();
                        return { 
                            id: docSnap.id,
                            chatName: groupData.title || "Unknown Group",
                            profilePic: groupData.image,
                            lastMessageTime: lastMessage?.timestamp,
                            isGroup: true,
                            groupId: groupDoc.id,
                            ...chatData 
                        };
                    }
                } catch (error) {
                    console.error('Error fetching group data:', error);
                }

                // Fallback for unknown group chats
                return { 
                    id: docSnap.id, 
                    chatName: "Unknown Group",
                    profilePic: null,
                    lastMessageTime: lastMessage?.timestamp,
                    isGroup: true,
                    ...chatData 
                };
            }));
            setConversations(chats);
        });

        return () => unsubscribe();
    }, [user]);

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        
        const messageDate = timestamp.toDate();
        const now = new Date();
        const diffMinutes = Math.floor((now - messageDate) / (1000 * 60));
        
        if (diffMinutes < 60) {
            return `${diffMinutes}m ago`;
        } else if (diffMinutes < 24 * 60) {
            const hours = Math.floor(diffMinutes / 60);
            return `${hours}h ago`;
        } else {
            return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    };

    // Fetch messages for the selected chat
    useEffect(() => {
        if (!selectedChat ) return;
        if (typeof selectedChat === 'object' && selectedChat.isNew) { 
            setMessages([]);
            return;
        }

        const messagesRef = collection(db, "conversations", selectedChat, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Get unique sender IDs from messages
            const senderIds = [...new Set(msgs.map(msg => msg.senderId))];
            
            // Fetch user info for all senders that haven't been fetched yet
            await Promise.all(senderIds.map(async (senderId) => {
                if (!senderNames[senderId]) {
                    await fetchUserInfo(senderId);
                }
            }));
            
            setMessages(msgs);
        });

        return () => unsubscribe();
    }, [selectedChat]);

    // Send a message
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [showFormattingMenu, setShowFormattingMenu] = useState(false);
    const emojiPickerRef = useRef(null);
    const inputRef = useRef(null);
    const formattingMenuRef = useRef(null);

    // Close formatting menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (formattingMenuRef.current && !formattingMenuRef.current.contains(event.target)) {
                setShowFormattingMenu(false);
            }
        };

        if (showEmojiPicker || showFormattingMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker, showFormattingMenu]);

    const toggleFormattingMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowFormattingMenu(prev => !prev);
    };

    const applyFormatting = (format) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selectedText = messageContent.substring(start, end);
        let newText = '';

        switch (format) {
            case 'bold':
                newText = `<b>${selectedText}</b>`;
                break;
            case 'italic':
                newText = `<i>${selectedText}</i>`;
                break;
            case 'underline':
                newText = `<u>${selectedText}</u>`;
                break;
            case 'strikethrough':
                newText = `<s>${selectedText}</s>`;
                break;
            default:
                return;
        }

        const newContent = messageContent.substring(0, start) + newText + messageContent.substring(end);
        setMessageContent(newContent);
        setShowFormattingMenu(false);
    };

    const toggleEmojiPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowEmojiPicker(prev => !prev);
    };

    const handleEmojiSelect = (emoji) => {
        const input = inputRef.current;
        if (input) {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const newValue = messageContent.substring(0, start) + emoji.native + messageContent.substring(end);
            setMessageContent(newValue);
            
            // Set cursor position after emoji
            setTimeout(() => {
                input.selectionStart = input.selectionEnd = start + emoji.native.length;
                input.focus();
            }, 0);
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!messageContent.trim() || !selectedChat) return;

        try {
            let chatId = selectedChat;
        
            if (typeof selectedChat === 'object' && selectedChat.isNew) {
                const newChatRef = doc(collection(db, "conversations"));
                await setDoc(newChatRef, {
                    participants: selectedChat.participants,
                    isGroup: false
                });
                chatId = newChatRef.id;
                setSelectedChat(chatId);
            }

            await addDoc(collection(db, "conversations", chatId, "messages"), {
                text: messageContent,
                senderId: user.uid,
                timestamp: new Date(),
                isFormatted: messageContent.includes('<b>') || messageContent.includes('<i>') || 
                           messageContent.includes('<u>') || messageContent.includes('<s>')
            });
            setMessageContent("");
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    // Start a new conversation
    const startConversation = async (receiverId) => {
        if (receiverId === user.uid) return;
        console.log("Closing drop down");
        setIsDropdownOpen(false);

        // Check if a conversation already exists
        const q = query(collection(db, "conversations"), 
            where("participants", "array-contains", user.uid));
        const existingChats = await getDocs(q);

        const chat = existingChats.docs.find(doc => {
            const chatData = doc.data();
            // Check that it is not a group chat and that the receiver is in the conversation
            return chatData.participants.includes(receiverId) && !chatData.isGroup;
        });
        
        if (chat) {
            console.log("Setting selected chat");
            setSelectedChat(chat.id);
        } else {
            console.log("setting!")
            setSelectedChat({ participants: [user.uid, receiverId], isNew: true});
            console.log("set!");
        }
    };

    // Add function to fetch user info
    const fetchUserInfo = async (userId) => {
        if (!userId || senderNames[userId]) return;
        try {
            const userRef = doc(db, "UserInformation", userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setSenderNames(prev => ({
                    ...prev,
                    [userId]: userData.name
                }));
                setSenderProfilePics(prev => ({
                    ...prev,
                    [userId]: userData.profilePictureUrl
                }));
            }
        } catch (error) {
            console.error("Error fetching user info:", error);
        }
    };

    // Modify renderMessage function
    const renderMessage = (msg) => {
        const isCurrentUser = msg.senderId === user.uid;
        const messageTime = msg.timestamp ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

        if (msg.type === 'shared_post') {
            return (
                <div key={msg.id} className={`message ${isCurrentUser ? "sent" : "received"}`}>
                    <div className="message-content">
                        {!isCurrentUser && (
                            <div className="message-sender">
                                <Avatar
                                    src={senderProfilePics[msg.senderId]}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        marginRight: 1,
                                        bgcolor: '#E9505F'
                                    }}
                                >
                                    {senderNames[msg.senderId]?.[0] || '?'}
                                </Avatar>
                                <span className="sender-name">{senderNames[msg.senderId] || 'Unknown'}</span>
                            </div>
                        )}
                        <div className="message-bubble">
                            <SharedPostLink 
                                postId={msg.postId}
                                caption={msg.caption}
                                senderName={msg.senderName}
                            />
                            <span className="message-time">{messageTime}</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={msg.id} className={`message ${isCurrentUser ? "sent" : "received"}`}>
                <div className="message-content">
                    {!isCurrentUser && (
                        <div className="message-sender">
                            <Avatar
                                src={senderProfilePics[msg.senderId]}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    marginRight: 1,
                                    bgcolor: '#E9505F'
                                }}
                            >
                                {senderNames[msg.senderId]?.[0] || '?'}
                            </Avatar>
                            <span className="sender-name">{senderNames[msg.senderId] || 'Unknown'}</span>
                        </div>
                    )}
                    <div className="message-bubble">
                        {msg.isFormatted ? (
                            <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                        ) : (
                            <p>{msg.text}</p>
                        )}
                        <span className="message-time">{messageTime}</span>
                    </div>
                </div>
            </div>
        );
    };

    const handleViewProfile = (userId) => {
        if (userId) {
            navigate(`/profile/${userId}`);
        }
    };

    return (
        <Layout pageTitle="Messages &#x1F4AC;">
            <div className="page-content">
                <div className="messages-container">
                    {/* Left Sidebar */}
                    <div className="sidebar">
                        <div className="search-bar-container">
                            <input
                                type="text"
                                className="search-bar"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={() => setIsFriendsDropdownOpen(true)}
                            />
                            {isFriendsDropdownOpen && suggestions.length > 0 && (
                                <div className="suggestions-dropdown" ref={friendsDropdownRef}>
                                    {suggestions.map((usr) => (
                                        <p key={usr.id} onClick={() => sendFriendRequest(usr.id)}>
                                            {usr.name}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <h3>Chats</h3>
                        <div className="dropdown-container" ref={dropdownRef}>
                            <button className={`new-chat-btn ${isDropdownOpen ? "active" : ""}`} 
                                    onClick={() => toggleDropdown()}>
                                ➕
                            </button>
                        </div>  

                        <div className="chat-list-scroll">
                        {conversations.map(chat => (
                            <p key={chat.id} onClick={() => setSelectedChat(chat.id)}>
                                <div className="chat-item">
                                    <Avatar
                                        src={chat.profilePic}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: '#E9505F'
                                        }}
                                    >
                                        {chat.chatName?.[0] || '?'}
                                    </Avatar>
                                    <div className="chat-item-info">
                                        <span className="chat-item-name">{chat.chatName}</span>
                                    </div>
                                </div>
                                {chat.lastMessageTime && (
                                    <span className="chat-item-time">
                                        {formatMessageTime(chat.lastMessageTime)}
                                    </span>
                                )}
                            </p>
                        ))}
                        </div>
                    </div>

                    {/* Chat Window */}
                    <div className="chat-window">
                        {selectedChat ? (
                            <>
                                {/* Chat Header */}
                                <div className="chat-header">
                                    {conversations.find(chat => chat.id === selectedChat) && (
                                        <>
                                            <div className="chat-user-info">
                                                <Avatar
                                                    src={conversations.find(chat => 
                                                        chat.id === selectedChat
                                                    )?.profilePic}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        marginRight: 1,
                                                        bgcolor: '#E9505F'
                                                    }}
                                                >
                                                    {conversations.find(chat => 
                                                        chat.id === selectedChat
                                                    )?.chatName[0] || '?'}
                                                </Avatar>
                                                <div className="user-status">
                                                    <span className="user-name">
                                                        {conversations.find(chat => 
                                                            chat.id === selectedChat
                                                        )?.chatName}
                                                    </span>
                                                    <span className="online-status">
                                                        <span className="status-dot"></span>
                                                        Online
                                                    </span>
                                                </div>
                                            </div>
                                            {!conversations.find(chat => 
                                                chat.id === selectedChat
                                            )?.isGroup && (
                                                <button 
                                                    className="view-profile-btn"
                                                    onClick={() => handleViewProfile(conversations.find(chat => chat.id === selectedChat)?.userId)}
                                                >
                                                    Profile
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="messages-list">
                                    {messages.map(renderMessage)}
                                </div>
                                <form onSubmit={sendMessage} className="message-input" style={{ padding: '1px' }}>
                                    <div className="message-input-container">
                                        <button 
                                            type="button"
                                            className="format-button" 
                                            onClick={toggleFormattingMenu}
                                        >
                                            ⋮
                                        </button>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={messageContent}
                                            onChange={(e) => setMessageContent(e.target.value)}
                                            placeholder="Type a message..."
                                        />
                                        <button 
                                            type="button"
                                            className="emoji-button" 
                                            onClick={toggleEmojiPicker}
                                        >
                                            😊
                                        </button>
                                        <button 
                                            type="submit"
                                            className="send-button" 
                                            disabled={!messageContent.trim()}
                                        >
                                            <img src={SendIcon} alt="Send" />
                                        </button>
                                        {showFormattingMenu && (
                                            <div className="formatting-menu" ref={formattingMenuRef}>
                                                <button onClick={() => applyFormatting('bold')} className="format-option">
                                                    <b>B</b>
                                                </button>
                                                <button onClick={() => applyFormatting('italic')} className="format-option">
                                                    <i>I</i>
                                                </button>
                                                <button onClick={() => applyFormatting('underline')} className="format-option">
                                                    <u>U</u>
                                                </button>
                                                <button onClick={() => applyFormatting('strikethrough')} className="format-option">
                                                    <s>S</s>
                                                </button>
                                            </div>
                                        )}
                                        {showEmojiPicker && (
                                            <div className="emoji-picker-container" ref={emojiPickerRef}>
                                                <Picker
                                                    data={data}
                                                    onEmojiSelect={handleEmojiSelect}
                                                    theme="light"
                                                    autoFocus={true}
                                                    emojiSize={20}
                                                    emojiButtonSize={28}
                                                    perLine={8}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </>
                        ) : (
                            <p>Select a chat to start messaging.</p>
                        )}
                    </div>
                </div>
                {showStatus && <div className="status-popup">{status}</div>}
            </div>
            {isMobile && !isFriendsDropdownOpen && !isDropdownOpen && (
            <div className="fab-container">
                <button className="fab-button search" onClick={() => setIsFriendsDropdownOpen(true)}>
                🔍
                </button>
                <button className="fab-button new-chat" onClick={() => toggleDropdown()}>
                ＋
                </button>
            </div>
            )}

            {isDropdownOpen && (
            <div className="mobile-popup-overlay">
                <div
                    className="mobile-popup" 
                    ref={dropdownRef}
                >
                    <div className="popup-header">
                        <h3>Select a friend to chat</h3>
                        <button
                            className="messages-popup-close"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="popup-scroll">
                        {allFriends.length > 0 ? (
                        allFriends.map((u) => (
                            <div key={u.id} className="popup-chat-item" onClick={() => {
                                startConversation(u.id)
                            }}>
                                <Avatar
                                    src={u.profilePictureUrl}
                                    sx={{ width: 40, height: 40, bgcolor: '#E9505F' }}
                                >
                                    {u.name?.[0] || '?'}
                                </Avatar>
                                <span>{u.name}</span>
                            </div>
                        ))
                        ) : (
                        <p className="no-friends">No friends available.</p>
                        )}
                    </div>
                </div>
            </div>
            )} 

            {isMobile && isFriendsDropdownOpen && (
            <div className="search-overlay">
                <input
                className="mobile-search-input"
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="close-search" onClick={() => {
                    setIsFriendsDropdownOpen(false);
                    setSearchTerm("");
                }}>✕</button>
                <div className="search-results">
                    {isFriendsDropdownOpen && suggestions.length > 0 && (
                        <div className="suggestions-dropdown mobile" ref={friendsDropdownRef}>
                            {suggestions.map((usr) => (
                                <p key={usr.id} onClick={() => {
                                    sendFriendRequest(usr.id);
                                }}>
                                    {usr.name}
                                </p>
                            ))}
                        </div>
                    )}
                    {searchTerm && suggestions.length === 0 && (
                        <p className="no-results">No matching users found.</p>
                    )}
                </div>
            </div>
            )}

        </Layout>
    );
};

export default MessagesPage;
